import React, { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTutorialStore } from './useTutorialStore';
import { getStepsForTutorial } from './tutorialSteps';

/** Step id -> { trigger selector, content selector, wrapper data-tutorial } for Views & cards combined highlights */
const COMBINED_HIGHLIGHT_CONFIG: Record<
  string,
  { trigger: string; content: string; wrapper: string; blueBoxUnion?: boolean }
> = {
  'card-config-icon': {
    trigger: '[data-tutorial="card-config-icon"]',
    content: '[data-tutorial="card-config-content"]',
    wrapper: 'card-config-combined-highlight',
  },
  'view-actions-menu': {
    trigger: '[data-tutorial="view-actions-menu"]',
    content: '[data-tutorial="view-actions-menu-content"]',
    wrapper: 'view-actions-combined-highlight',
  },
  'add-viz-menu': {
    trigger: '[data-tutorial="add-viz-menu"]',
    content: '[data-tutorial="add-viz-menu-content"]',
    wrapper: 'add-viz-combined-highlight',
  },
  'load-view-menu': {
    trigger: '[data-tutorial="load-view-menu"]',
    content: '[data-tutorial="load-view-menu-content"]',
    wrapper: 'load-view-combined-highlight',
  },
  'refresh-view-menu': {
    trigger: '[data-tutorial="refresh-view-menu"]',
    content: '[data-tutorial="refresh-view-menu-content"]',
    wrapper: 'refresh-view-combined-highlight',
  },
  // Filters tutorial: Step 4 – highlight date range link + popover together
  'date-range-trigger': {
    trigger: '[data-tutorial="date-range-trigger"]',
    content: '[data-tutorial="date-range-popover"]',
    wrapper: 'date-range-trigger-combined-highlight',
  },
  // Account menu (API Overview, Dashboard Overview, Account & Settings): icon + dropdown
  'account-menu': {
    trigger: '[data-tutorial="account-menu"]',
    content: '[data-tutorial="account-menu-content"]',
    wrapper: 'account-menu-combined-highlight',
  },
  // Node Graph Overview: active graph tab + workspace (same step as workspace copy)
  'node-graph-open': {
    trigger: '[data-tutorial="node-graph-active-tab"]',
    content: '[data-tutorial="network-chart-workspace"]',
    wrapper: 'node-graph-open-combined-highlight',
  },
  'node-graph-context-menu': {
    trigger: '[data-tutorial="network-chart-tutorial-context-node-frame"]',
    content: '[data-tutorial="network-chart-context-menu"]',
    wrapper: 'node-graph-context-menu-combined-highlight',
    blueBoxUnion: true,
  },
};

function measureUnion(triggerSel: string, contentSel: string): { union: DOMRect; trigger: DOMRect } | null {
  const trigger = document.querySelector(triggerSel);
  const content = document.querySelector(contentSel);
  if (!trigger || !content) return null;
  const r1 = trigger.getBoundingClientRect();
  const r2 = content.getBoundingClientRect();
  const left = Math.min(r1.left, r2.left);
  const top = Math.min(r1.top, r2.top);
  const right = Math.max(r1.right, r2.right);
  const bottom = Math.max(r1.bottom, r2.bottom);
  const union = new DOMRect(left, top, right - left, bottom - top);
  return { union, trigger: r1 };
}

/**
 * When the Views & cards tutorial is on a step that has both an icon and a menu/content,
 * renders a single div positioned to encompass both so driver.js can highlight icon + menu together.
 * Rendered in a portal so it can sit above the UI with position:fixed.
 */
export const TutorialCombinedHighlight: React.FC = () => {
  const { isRunning, currentTutorialId, stepIndex } = useTutorialStore();
  const steps = currentTutorialId ? getStepsForTutorial(currentTutorialId) : [];
  const currentStepId = steps[stepIndex]?.id;
  const config = currentStepId ? COMBINED_HIGHLIGHT_CONFIG[currentStepId] : null;

  const [measured, setMeasured] = useState<{ union: DOMRect; trigger: DOMRect } | null>(null);
  const [wrapperId, setWrapperId] = useState<string | null>(null);

  useLayoutEffect(() => {
    if (!isRunning || !config) {
      setMeasured(null);
      setWrapperId(null);
      return;
    }

    const update = () => {
      const result = measureUnion(config.trigger, config.content);
      setMeasured(result);
      setWrapperId(config.wrapper);
    };

    update();
    const raf = requestAnimationFrame(update);
    const t = setTimeout(update, 50);
    // Load View (step 6): Popover content may portal in after open; retry so icon + menu highlight appears like steps 4, 5, 7
    const tLate =
      currentStepId === 'load-view-menu' ||
      currentStepId === 'node-graph-open' ||
      currentStepId === 'node-graph-context-menu'
        ? setTimeout(update, 100)
        : currentStepId === 'date-range-trigger'
          ? setTimeout(update, 100)
          : undefined;
    const tLate2 =
      currentStepId === 'load-view-menu' ||
      currentStepId === 'node-graph-open' ||
      currentStepId === 'node-graph-context-menu'
        ? setTimeout(update, 200)
        : undefined;
    const tLate3 =
      currentStepId === 'node-graph-context-menu' ? setTimeout(update, 400) : undefined;
    const resize = () => update();
    window.addEventListener('resize', resize);
    window.addEventListener('scroll', resize, true);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
      if (tLate !== undefined) clearTimeout(tLate);
      if (tLate2 !== undefined) clearTimeout(tLate2);
      if (tLate3 !== undefined) clearTimeout(tLate3);
      window.removeEventListener('resize', resize);
      window.removeEventListener('scroll', resize, true);
    };
  }, [isRunning, currentTutorialId, currentStepId, config]);

  if (!config || !measured || typeof document === 'undefined') return null;

  const { union, trigger } = measured;
  const blueBox = config.blueBoxUnion ? union : trigger;

  const el = (
    <>
      {/* Driver.js targets this for the full highlight (icon + menu) */}
      <div
        data-tutorial={wrapperId ?? undefined}
        aria-hidden
        style={{
          position: 'fixed',
          left: union.left,
          top: union.top,
          width: union.width,
          height: union.height,
          pointerEvents: 'none',
          zIndex: 9998,
        }}
      />
      {/* Blue box: trigger only, or full union (e.g. node + context menu) */}
      <div
        aria-hidden
        style={{
          position: 'fixed',
          left: blueBox.left,
          top: blueBox.top,
          width: blueBox.width,
          height: blueBox.height,
          pointerEvents: 'none',
          zIndex: 9999,
          border: '2px solid #2563eb',
          borderRadius: 4,
          boxSizing: 'border-box',
        }}
      />
    </>
  );

  return createPortal(el, document.body);
};
